// AI Connections — "session control". Lists the AI clients (Claude,
// ChatGPT, Codex …) the current admin has authorized against the remote
// MCP server, and lets them revoke a grant (kills every access/refresh
// token in the family). Data: GET /admin/ai/connections (scoped to the
// admin server-side); revoke: POST /admin/ai/connections/{id}/revoke.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { ConnectAssistantModal } from "@/components/ai/ConnectAssistantModal";
import { fmtRelative } from "@/lib/format";

interface Connection {
  connection_id: string;
  client_id: string;
  client_name: string;
  admin_email: string;
  scopes: string[];
  created_at: string;
  last_used_at?: string;
  expires_at: string;
  token_count: number;
}

export const Route = createFileRoute("/_app/ai/connections")({
  component: ConnectionsPage,
});

function ConnectionsPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["ai", "connections"],
    queryFn: () => api<{ connections: Connection[] }>("/admin/ai/connections"),
    refetchInterval: 30_000,
  });
  const [revoking, setRevoking] = useState<Connection | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);

  const revoke = useMutation({
    mutationFn: (id: string) =>
      api(`/admin/ai/connections/${id}/revoke`, { method: "POST" }),
    onSuccess: async () => {
      setRevoking(null);
      await qc.invalidateQueries({ queryKey: ["ai", "connections"] });
    },
    meta: { successMessage: "Connection revoked." },
  });

  const connections = q.data?.connections ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-t-1">AI Connections</h1>
          <p className="text-sm text-muted">
            AI assistants you have connected to the admin tools. Each acts under your own
            permissions. Revoke a connection to sign it out immediately.
          </p>
        </div>
        <Button onClick={() => setConnectOpen(true)}>Connect an assistant</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active connections</CardTitle>
        </CardHeader>
        <CardBody>
          {q.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : connections.length === 0 ? (
            <EmptyState
              title="No active connections"
              description="Connect an AI assistant from Claude, ChatGPT or Codex using the MCP server URL to see it here."
              action={
                <Button onClick={() => setConnectOpen(true)}>Connect an assistant</Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-t-3">
                    <th className="py-2 pr-4">Client</th>
                    <th className="py-2 pr-4">Scopes</th>
                    <th className="py-2 pr-4">Connected</th>
                    <th className="py-2 pr-4">Last used</th>
                    <th className="py-2 pr-4">Expires</th>
                    <th className="py-2 pr-4" />
                  </tr>
                </thead>
                <tbody>
                  {connections.map((c) => (
                    <tr key={c.connection_id} className="border-t border-line">
                      <td className="py-3 pr-4">
                        <div className="font-medium text-t-1">{c.client_name}</div>
                        <div className="font-mono text-xs text-muted">{c.client_id}</div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex flex-wrap gap-1">
                          {c.scopes.map((s) => (
                            <Badge key={s} tone="muted">
                              {s}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-t-2">{fmtRelative(c.created_at)}</td>
                      <td className="py-3 pr-4 text-t-2">
                        {c.last_used_at ? fmtRelative(c.last_used_at) : "—"}
                      </td>
                      <td className="py-3 pr-4 text-t-2">{fmtRelative(c.expires_at)}</td>
                      <td className="py-3 pr-4 text-right">
                        <Button variant="danger" size="sm" onClick={() => setRevoking(c)}>
                          Revoke
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <ConfirmModal
        open={revoking !== null}
        title="Revoke this connection?"
        tone="danger"
        confirmLabel="Revoke"
        description={
          revoking ? (
            <span>
              <span className="font-semibold text-t-1">{revoking.client_name}</span> will lose
              access immediately. Any in-progress AI session using it will stop working. This
              cannot be undone — you'd have to reconnect.
            </span>
          ) : undefined
        }
        loading={revoke.isPending}
        error={revoke.isError ? (revoke.error as Error).message : null}
        onCancel={() => setRevoking(null)}
        onConfirm={() => revoking && revoke.mutate(revoking.connection_id)}
      />

      {connectOpen && <ConnectAssistantModal onClose={() => setConnectOpen(false)} />}
    </div>
  );
}
